import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Form, Button, Badge, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAllUsers } from '../services/userService';
import dayjs from 'dayjs';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtreleme
  const [searchName, setSearchName] = useState('');
  const [filterUserType, setFilterUserType] = useState('');

  // İstatistikler
  const [stats, setStats] = useState({
    total: 0,
    byUserType: {}
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const userList = await getAllUsers();
      setUsers(userList);
      setFilteredUsers(userList);

      // İstatistikleri hesapla
      const byUserType = {};
      userList.forEach(user => {
        const typeName = user.userTypeDto?.name || 'Belirsiz';
        byUserType[typeName] = (byUserType[typeName] || 0) + 1;
      });

      setStats({
        total: userList.length,
        byUserType
      });
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
      toast.error('Kullanıcılar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filtreleme
  useEffect(() => {
    let filtered = users;

    if (searchName) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (filterUserType) {
      filtered = filtered.filter(user =>
        user.userTypeDto?.name === filterUserType
      );
    }

    setFilteredUsers(filtered);
  }, [users, searchName, filterUserType]);

  const resetFilters = () => {
    setSearchName('');
    setFilterUserType('');
  };

  const userTypes = [...new Set(users.map(u => u.userTypeDto?.name).filter(Boolean))];

  const getUserTypeBadge = (userType) => {
    const colors = {
      'Admin': 'danger',
      'User': 'primary',
      'Premium': 'warning',
      'Standart': 'info'
    };
    return colors[userType] || 'secondary';
  };

  return (
    <div className="container-fluid p-4">
      {/* Başlık ve İstatistikler */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-primary">
            <Card.Body className="text-center">
              <div className="h2 text-primary">{stats.total}</div>
              <p className="mb-0">Toplam Kullanıcı</p>
            </Card.Body>
          </Card>
        </Col>
        {Object.entries(stats.byUserType).slice(0, 3).map(([type, count]) => (
          <Col md={3} sm={6} className="mb-3" key={type}>
            <Card className="h-100">
              <Card.Body className="text-center">
                <div className="h2">
                  <Badge bg={getUserTypeBadge(type)}>{count}</Badge>
                </div>
                <p className="mb-0">{type}</p>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Kullanıcı Listesi</h4>
            <Link to="/user-management" className="btn btn-primary">
              Admin Yönetimi
            </Link>
          </div>
        </Card.Header>

        <Card.Body>
          <Alert variant="info" className="mb-4">
            <strong>Gizlilik Bildirimi:</strong> Kullanıcıların kişisel bilgileri (telefon, e-posta, cinsiyet, doğum tarihi) gizlilik nedeniyle gösterilmemektedir.
          </Alert>

          {/* Filtreleme Alanı */}
          <Row className="mb-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Ad Ara</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Kullanıcı adı..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Kullanıcı Tipi</Form.Label>
                <Form.Select
                  value={filterUserType}
                  onChange={(e) => setFilterUserType(e.target.value)}
                >
                  <option value="">Tümü</option>
                  {userTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>&nbsp;</Form.Label>
                <div className="d-grid">
                  <Button variant="outline-secondary" onClick={resetFilters}>
                    Filtreleri Temizle
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* Sonuç Bilgisi */}
          <Row className="mb-3">
            <Col>
              <Alert variant="secondary" className="mb-0">
                <strong>Toplam:</strong> {stats.total} kullanıcı |
                <strong> Görüntülenen:</strong> {filteredUsers.length} kullanıcı
              </Alert>
            </Col>
          </Row>

          {/* Tablo */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </Spinner>
            </div>
          ) : (
            <Table bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '30%' }}>Ad</th>
                  <th style={{ width: '20%' }}>Kullanıcı Tipi</th>
                  <th style={{ width: '25%' }}>Durum</th>
                  <th style={{ width: '25%' }}>Kayıt Tarihi</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-4">
                      Kullanıcı bulunamadı.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.name || '-'}</strong>
                      </td>
                      <td>
                        <Badge bg={getUserTypeBadge(user.userTypeDto?.name)}>
                          {user.userTypeDto?.name || 'Belirsiz'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={user.userStatusDto?.name === 'Approved' ? 'success' : 'warning'}>
                          {user.userStatusDto?.description || user.userStatusDto?.name || '-'}
                        </Badge>
                      </td>
                      <td>
                        {user.createTime ? dayjs(user.createTime).format('DD.MM.YYYY') : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Users;
